import { onUnmounted, Ref, watch } from 'vue';

/**
 * Returns the scroll parent of the given element.
 * @param {HTMLElement} node
 * @return {HTMLElement}
 */
export function getScrollParent(node: HTMLElement): Element {
  const regex = /(auto|scroll)/;
  function parents(element: HTMLElement | ParentNode | null | undefined, ps: ParentNode[]): ParentNode[] {
    if (element?.parentNode === null) {
      return ps;
    }
    return parents(element?.parentNode, element ? ps.concat([element]) : ps);
  }

  function style(element: HTMLElement, prop: string) {
    return getComputedStyle(element, null).getPropertyValue(prop);
  }

  function overflow(element: HTMLElement) {
    return style(element, 'overflow') + style(element, 'overflow-y') + style(element, 'overflow-x');
  }

  function scroll(element: HTMLElement) {
    return regex.test(overflow(element));
  }

  function scrollParent(element: HTMLElement | SVGElement): Element {
    if (!(element instanceof HTMLElement || element instanceof SVGElement)) {
      return document.documentElement;
    }

    const ps = parents(element.parentNode, []);

    for (let i = 0; i < ps.length; i += 1) {
      if (ps[i] instanceof HTMLElement && scroll(ps[i] as HTMLElement)) {
        return ps[i] as HTMLElement;
      }
    }

    return document.scrollingElement || document.documentElement;
  }

  return scrollParent(node) || document.documentElement;
}

/**
 * A composable to lock and unlock page scroll.
 * @param {Ref<boolean>} isOpen - A ref that controls the locked state.
 */
export function useScrollLock(isOpen: Ref<boolean>, element: HTMLElement = document.body) {
  let scrollY = '0px';
  const scrollableEl = getScrollParent(element);

  function lockScroll() {
    scrollY = `${scrollableEl.scrollTop}px`;
    scrollableEl.classList.add('axitech-widget-modal-open');
    if ('style' in scrollableEl && scrollableEl instanceof HTMLElement) {
      scrollableEl.style.top = `-${scrollY}`;
    }
  }

  function unlockScroll() {
    scrollableEl.classList.remove('axitech-widget-modal-open');
    if ('style' in scrollableEl && scrollableEl instanceof HTMLElement) {
      scrollableEl.style.top = '';
    }
    scrollableEl.scrollTop = parseInt(scrollY || '0');
  }

  watch(
    () => isOpen.value,
    (isModalOpen) => {
      if (typeof window === 'undefined') return;
      if (isModalOpen) {
        lockScroll();
      } else {
        unlockScroll();
      }
    },
    { immediate: true }
  );

  onUnmounted(() => {
    if (isOpen.value) {
      unlockScroll();
    }
  });

  return {
    lockScroll,
    unlockScroll,
  };
}
